// MARK: NPM Modules
import React from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import ReduxStyleguide from "types/redux/styleguide";
// MARK: Components
import Typography from "components/common/typography";
// MARK: Functionality
import Media from "utils/common/media";
import OutcomesGridHeroItem from "types/fields/matrix/grids/outcomesGridHeroItem";
import OutcomesGridTextItem from "types/fields/matrix/grids/outcomesGridTextItem";
import CSSStyles from "enums/cssStyles";

// MARK: Styled Components
interface ItemProps {
  name: string;
}

const Item = styled.li<ItemProps>`
  position: relative;
  display: inline-block;
`;

interface MediaContainerProps {
  margin: number;
  height: number;
}

const MediaContainer = styled.div<MediaContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${({ height }) => height}px;
  margin-bottom: ${({ margin }) => margin}px;
  transition: 0.6s cubic-bezier(0.19, 1, 0.22, 1);
  overflow: hidden;
`;

// MARK: React Component
// Props
interface GridItemProps {
  item: OutcomesGridHeroItem | OutcomesGridTextItem;
  itemWidth: number;
  HTMLHeaderType: string;
}
// Styleguide & Datapoints
const getDataPoints = (styleguide: ReduxStyleguide) => {
  const { colors } = styleguide;
  const { primary, secondary, tertiary } = colors;
  return {
    headlineStyle: CSSStyles.large,
    headlineColor: primary,
    descriptionStyle: CSSStyles.sectionSubtitle,
    descriptionColor: tertiary,
    bodyStyle: CSSStyles.body,
    bodyColor: secondary,
  };
};
// Component
const ListItem = ({
  item,
  itemWidth,
  HTMLHeaderType,
}: GridItemProps) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: State Variables
  // MARK: Variables
  const { margin, mobile } = styleguide;
  const {
    headlineStyle,
    headlineColor,
    descriptionStyle,
    descriptionColor,
    bodyStyle,
    bodyColor,
  } = getDataPoints(styleguide);
  // MARK: Functionality

  const renderHero = () => {
    switch (item.typeHandle) {
      case "textOutcome":
        const element = item as OutcomesGridTextItem;
        return (
          <MediaContainer
            key={"media"}
            margin={margin}
            height={(itemWidth * 2) / 3}
          >
            <Typography
              className={headlineStyle}
              color={headlineColor}
              textAlign={"center"}
              formatCopy
            >
              {element.headline}
            </Typography>
          </MediaContainer>
        );
        break;
      case "heroOutcome":
        const heroOutcome = item as OutcomesGridHeroItem;
        return (
          <MediaContainer
            key={"media"}
            margin={margin}
            height={(itemWidth * 2) / 3}
          >
            <Media hero={heroOutcome.hero} borderradius={20} />
          </MediaContainer>
        );
    }
  };

  const renderDescription = () => {
    var scene: Array<any> = [];
    scene.push(
      <Typography
        key={"description"}
        type={HTMLHeaderType}
        marginTop={margin}
        minheight={mobile ? 0 : 100}
        className={descriptionStyle}
        color={descriptionColor}
        textAlign={"left"}
        formatCopy
      >
        {item.description}
      </Typography>
    );
    scene.push(
      <Typography
        key={"body"}
        marginTop={mobile ? margin : 0}
        className={bodyStyle}
        color={bodyColor}
        textAlign={"left"}
        formatCopy
      >
        {item.body}
      </Typography>
    );
    return scene;
  };

  // MARK: Render
  return (
    <Item
      key={item.title}
      aria-label={item.title}
      itemProp="name"
      role={"listitem"}
      name={item.title}
    >
      {renderHero()}
      {renderDescription()}
    </Item>
  );
};

export default ListItem;
